/* eslint-disable react/require-default-props */
/* eslint-disable max-statements-per-line */
/* eslint-disable max-len */
import type { ComponentProps } from 'react';
import { useMemo, useEffect, useState } from 'react';
// eslint-disable-next-line import/no-named-as-default
import toast from 'react-hot-toast';
import Select from 'react-select';

import {
    Button,
    ButtonVariant,
    BarChart,
    Toast,
    transformCurrency,
} from '@catalyze/ui';
import type { IPropertyCount } from 'api/products/getProducts';

import {
    BillingPeriod,
    BillingPeriod2,
    Price,
    Price2,
    Title,
    Container,
    Row,
    FormSubtitle,
} from './styled';

type IPlanProps = ComponentProps<typeof Container> & {
    name: string;
    price?: string;
    billingPeriod?: string;
    onSubscribe: (count?: number, name?: string) => void;
    loading?: boolean;
    useCount?: boolean;
    count?: number;
    description?: string;
    zip?: string;
    subscriptionId?: string;
    propertyDistribution?: IPropertyCount[];
    onProductSelectionUpdate?: (count: number, isAddedToCart: boolean) => void;
};

export const Plan = ({
    name,
    price,
    billingPeriod,
    onSubscribe,
    loading,
    useCount,
    onProductSelectionUpdate,
    description,
    subscriptionId,
    count,
    zip,
    propertyDistribution,
    ...props
}: IPlanProps): JSX.Element => {
    const [selectedAmount, setSelectedAmount] = useState(0);
    const [isAddedToCart, setIsAddedToCart] = useState(false);

    useEffect(() => {
        if (onProductSelectionUpdate) {
            onProductSelectionUpdate(selectedAmount, isAddedToCart);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAmount, isAddedToCart]);

    useEffect(() => {
        if (onProductSelectionUpdate) {
            onProductSelectionUpdate(0, false);
            setIsAddedToCart(false);
            setSelectedAmount(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count]);

    useEffect(() => {
        setIsAddedToCart(false);
        setSelectedAmount(0);
    }, [zip]);

    const countOptions: Array<{ value: number; label: string }> =
        useMemo(() => {
            // @ts-expect-error err
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands, prefer-const
            let seats = new Array((count || 0) <= 0 ? 0 : count + 1)
                .fill(0)
                .map((_, i) => ({ value: i, label: `${i * 10} leads` }));

            if (seats.length > 3) {
                // only show 0 & 3+ seat options if there are 3 or more seats
                seats.splice(1, 2);
            }
            return seats;
        }, [count]);

    const handleSubmit = () => {
        if (useCount && selectedAmount > 0) {
            onSubscribe(selectedAmount, name);
        } else if (useCount && selectedAmount <= 0) {
            toast(() => <Toast title="Please select a product quantity" />, {
                style: { backgroundColor: 'red' },
            });
        } else {
            onSubscribe();
        }
    };

    const isActionable = price && billingPeriod;

    return (
        <Container {...props}>
            <Title>{name}</Title>
            {isActionable ? (
                <Row>
                    <Price2>{transformCurrency(price)}</Price2>
                    <BillingPeriod2>/{billingPeriod}/10 leads</BillingPeriod2>
                </Row>
            ) : null}
            {description && <FormSubtitle>{description}</FormSubtitle>}
            {isActionable ? (
                <>
                    <Row>
                        <Price>
                            {transformCurrency(
                                (
                                    parseInt(price, 10) * selectedAmount
                                ).toString()
                            )}
                        </Price>
                        <BillingPeriod>/{billingPeriod} total</BillingPeriod>
                    </Row>
                    <Row>
                        {count ? (
                            <Select
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                options={countOptions}
                                placeholder="Select #"
                                value={{
                                    value: selectedAmount,
                                    label: `${selectedAmount * 10} leads`,
                                }}
                                // eslint-disable-next-line id-length, @typescript-eslint/no-explicit-any
                                onChange={(e: any) => {
                                    setSelectedAmount(e.value);
                                }}
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        // none of react-select's styles are passed to <Control />
                                        width: 200,
                                        marginLeft: 10,
                                    }),
                                    dropdownIndicator: (provided) => ({
                                        ...provided,
                                        color: 'white',
                                    }),
                                    indicatorsContainer: (provided) => ({
                                        ...provided,
                                        backgroundColor: 'lightgreen',
                                    }),
                                }}
                            />
                        ) : null}
                    </Row>
                </>
            ) : null}

            {subscriptionId && propertyDistribution?.length && (
                <BarChart
                    id={subscriptionId}
                    width={400}
                    height={300}
                    data={propertyDistribution}
                />
            )}
            {isActionable && useCount ? (
                <Button
                    variant={ButtonVariant.PRIMARY}
                    onClick={() => {
                        setIsAddedToCart(!isAddedToCart);
                    }}
                    disabled={loading || (count && selectedAmount === 0)}
                >
                    {isAddedToCart ? 'Remove From Cart ' : 'Add to Cart'}
                </Button>
            ) : null}
            {isActionable && !useCount ? (
                <Button
                    variant={ButtonVariant.PRIMARY}
                    onClick={handleSubmit}
                    disabled={loading || (count && selectedAmount === 0)}
                >
                    Subscribe
                </Button>
            ) : null}
        </Container>
    );
};
